import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useRef, useEffect, useState, useCallback } from 'react';

interface VirtualizedEventListProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  estimateSize?: number;
  overscan?: number;
  enabled?: boolean;
  gap?: number;
  isVisible?: boolean; // Track if parent section is visible
}

/**
 * Virtualized list that works with window scroll (not a container)
 * Only renders visible items for better performance
 * Seamlessly integrates with page scroll - no visible containers
 */
export function VirtualizedEventList<T>({
  items,
  renderItem,
  estimateSize = 180, // Estimated height of each event card
  overscan = 5, // Number of items to render outside viewport
  enabled = true,
  gap = 12, // Gap between items in pixels (gap-3 = 12px)
  isVisible = true, // Default to visible
}: VirtualizedEventListProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [scrollMargin, setScrollMargin] = useState(0);
  const scrollMarginRef = useRef(0);
  const measurementCacheRef = useRef<Map<number, number>>(new Map());
  // Clear cache when section becomes visible after being hidden
  useEffect(() => {
    if (isVisible) {
      // Clear measurement cache when section becomes visible to force fresh measurements
      measurementCacheRef.current.clear();
    }
  }, [isVisible]);

  // Helper function to round values to whole pixels to prevent subpixel rendering
  const roundToPixel = (value: number): number => Math.round(value);

  // Calculate total estimated size including gaps - round to whole pixels
  const itemSizeWithGap = roundToPixel(estimateSize + gap);

  // If virtualization is disabled or items are few, render normally
  const shouldVirtualize = enabled && items.length > 10 && typeof window !== 'undefined';
  
  // Stable scroll margin calculation - only update when significantly changed
  const updateScrollMargin = useCallback(() => {
    if (parentRef.current && typeof window !== 'undefined') {
      try {
        const rect = parentRef.current.getBoundingClientRect();
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const margin = Math.max(0, rect.top + scrollTop);
        const roundedMargin = roundToPixel(margin);
        
        // Only update if changed significantly (more than 10px) to prevent jitter
        if (Math.abs(roundedMargin - scrollMarginRef.current) > 10) {
          scrollMarginRef.current = roundedMargin;
          setScrollMargin(roundedMargin);
        }
        if (!isReady) {
          setIsReady(true);
        }
      } catch (e) {
        console.error('Error calculating scroll margin:', e);
      }
    }
  }, [isReady]);
  
  // Calculate scroll margin (container's offset from top of document)
  useEffect(() => {
    if (!shouldVirtualize || !isVisible) {
      if (!shouldVirtualize) {
        setIsReady(true);
      }
      // Clear ready state when section becomes hidden to force recalculation when visible again
      if (!isVisible && isReady) {
        setIsReady(false);
      }
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    // Initial calculation - wait for layout to settle
    const timeoutId = setTimeout(() => {
      updateScrollMargin();
    }, 150);
    
    // Update on resize and when visibility changes
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateScrollMargin, 100);
    };
    
    window.addEventListener('resize', handleResize, { passive: true });
    
    // Also update when section visibility changes (e.g., hide/show button)
    const visibilityTimeout = setTimeout(() => {
      updateScrollMargin();
    }, 200);
    
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(resizeTimeout);
      clearTimeout(visibilityTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [shouldVirtualize, isVisible, updateScrollMargin, isReady]);

  // Only create virtualizer when ready and should virtualize
  const virtualizer = useWindowVirtualizer({
    count: items.length,
    estimateSize: (index) => {
      // Use cached measurement if available, otherwise use estimate
      // Round to whole pixels to prevent subpixel rendering
      const size = measurementCacheRef.current.get(index) ?? itemSizeWithGap;
      return roundToPixel(size);
    },
    overscan,
    enabled: shouldVirtualize && isReady && isVisible,
    scrollMargin: isReady ? roundToPixel(scrollMargin) : 0,
    // Measure elements with caching to prevent jitter
    measureElement: (element) => {
      if (!element) return itemSizeWithGap;
      
      // Don't measure if section is not visible (elements are hidden)
      if (!isVisible) {
        return itemSizeWithGap;
      }
      
      const dataIndex = element.getAttribute('data-index');
      if (dataIndex !== null) {
        const idx = parseInt(dataIndex, 10);
        if (!isNaN(idx)) {
          // Check cache first
          const cached = measurementCacheRef.current.get(idx);
          if (cached && cached > 0) {
            // Round cached value to whole pixels
            return roundToPixel(cached);
          }
          
          // Measure and cache - round to whole pixels
          const height = element.getBoundingClientRect().height;
          if (height > 0) {
            const roundedHeight = roundToPixel(height);
            measurementCacheRef.current.set(idx, roundedHeight);
            return roundedHeight;
          }
        }
      }
      
      // Fallback to measurement - round to whole pixels
      const height = element.getBoundingClientRect().height;
      return height > 0 ? roundToPixel(height) : itemSizeWithGap;
    },
  });

  // Clear cache when items change or when section becomes visible after being hidden
  useEffect(() => {
    measurementCacheRef.current.clear();
  }, [items.length]);


  // Batch measurements to prevent jitter - measure after render settles
  useEffect(() => {
    if (!shouldVirtualize || !isReady || !isVisible) {
      return;
    }

    // Delay measurement to allow DOM to fully render and settle
    // This prevents jitter from rapid remeasurements
    const measureTimeout = setTimeout(() => {
      requestAnimationFrame(() => {
        if (virtualizer) {
          virtualizer.measure();
        }
      });
    }, 200); // Wait for layout to fully settle

    return () => {
      clearTimeout(measureTimeout);
    };
  }, [items.length, shouldVirtualize, isReady, isVisible, virtualizer]);

  // Don't render at all when section is hidden (prevents initialization issues)
  // Also clear cache when hidden to ensure fresh state when visible again
  if (!isVisible) {
    if (measurementCacheRef.current.size > 0) {
      measurementCacheRef.current.clear();
    }
    return null;
  }
  
  // If virtualization is disabled or items are few, render normally
  if (!shouldVirtualize || !isReady) {
    return (
      <div 
        ref={parentRef} 
        className="grid gap-3"
        style={{
          // Prevent blurriness from container transforms
          transform: 'translateZ(0)',
          WebkitFontSmoothing: 'antialiased',
        }}
      >
        {items.map((item, index) => (
          <div 
            key={index}
            style={{
              // Prevent blurriness from subpixel rendering
              transform: 'translate3d(0, 0, 0)',
            }}
          >
            {renderItem(item, index)}
          </div>
        ))}
      </div>
    );
  }

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  // Calculate padding for top and bottom to maintain scroll position
  // paddingTop should be the space needed for items before the first visible item
  // Only add padding if:
  // 1. There are visible items
  // 2. The first visible item is not at index 0 (meaning there are items before it)
  // 3. The start position is greater than 0
  const firstItemIndex = virtualItems.length > 0 ? virtualItems[0].index : -1;
  const firstItemStart = virtualItems.length > 0 ? (virtualItems[0]?.start ?? 0) : 0;
  const paddingTop = firstItemIndex > 0 && firstItemStart > 0
    ? roundToPixel(firstItemStart)
    : 0;
  
  const paddingBottom =
    virtualItems.length > 0
      ? roundToPixel(totalSize - (virtualItems[virtualItems.length - 1]?.end ?? 0))
      : 0;

  return (
    <div 
      ref={parentRef} 
      className="grid gap-3"
      style={{
        // Prevent blurriness from container transforms
        transform: 'translateZ(0)',
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      {/* Top padding to maintain scroll position - only if not starting from first item */}
      {paddingTop > 0 && <div style={{ height: paddingTop }} aria-hidden="true" />}
      
      {/* Render only visible items */}
      {virtualItems.map((virtualItem) => {
        const item = items[virtualItem.index];
        const cachedHeight = measurementCacheRef.current.get(virtualItem.index);
        // Round all pixel values to prevent subpixel rendering and blurriness
        const roundedHeight = cachedHeight && cachedHeight > 0 ? roundToPixel(cachedHeight) : undefined;
        return (
          <div
            key={virtualItem.key}
            data-index={virtualItem.index}
            ref={virtualizer.measureElement}
            style={{
              // Use cached height as min-height to prevent layout shift during remeasurements
              // Round to whole pixels to prevent subpixel rendering and blurriness
              minHeight: roundedHeight ? `${roundedHeight}px` : undefined,
            }}
          >
            {renderItem(item, virtualItem.index)}
          </div>
        );
      })}
      
      {/* Bottom padding to maintain scroll position */}
      {paddingBottom > 0 && <div style={{ height: paddingBottom }} aria-hidden="true" />}
    </div>
  );
}

